import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Trophy, Sparkles, Clock, Users, Coins } from "lucide-react";
import { Button } from "@/components/ui/button";
import { API, useAuth } from "@/App";

function timeRemaining(endIso) {
  if (!endIso) return null;
  const end = new Date(endIso).getTime();
  const now = Date.now();
  const diff = end - now;
  if (diff <= 0) return "Ended";
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  if (days > 0) return `${days}d ${hours}h left`;
  const mins = Math.floor((diff % 3600000) / 60000);
  return `${hours}h ${mins}m left`;
}

export default function Contests() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [active, setActive] = useState([]);
  const [past, setPast] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    Promise.all([
      fetch(`${API}/contests/active`).then(r => r.json()).catch(() => ({ contests: [] })),
      fetch(`${API}/contests/past`).then(r => r.json()).catch(() => ({ contests: [] })),
      user
        ? fetch(`${API}/admin/me`, { credentials: "include" }).then(r => r.ok ? r.json() : { is_admin: false }).catch(() => ({ is_admin: false }))
        : Promise.resolve({ is_admin: false }),
    ]).then(([a, p, am]) => {
      const contests = a.contests || [];
      // Fetch sponsors for each active contest in parallel
      return Promise.all(
        contests.map(c =>
          fetch(`${API}/contests/${c.contest_id}/sponsorships`)
            .then(r => r.ok ? r.json() : { sponsorships: [] })
            .then(sp => ({ ...c, sponsors: sp.sponsorships || [] }))
            .catch(() => ({ ...c, sponsors: [] }))
        )
      ).then(withSponsors => {
        setActive(withSponsors);
        setPast(p.contests || []);
        setIsAdmin(am.is_admin === true);
        setLoading(false);
      });
    });
  }, [user]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0a0014] via-[#1a0a2e] to-[#000] py-10 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <Trophy className="w-10 h-10 text-yellow-400" />
            <div>
              <h1 className="text-4xl md:text-5xl font-black text-white tracking-tight">
                Weekly Contests
              </h1>
              <p className="text-pink-300 text-sm">Win coins · Stack tips · Get featured</p>
            </div>
          </div>
          {isAdmin && (
            <Button
              onClick={() => navigate("/admin/contests")}
              className="bg-yellow-400 text-black hover:bg-yellow-300 font-bold border-2 border-black"
              data-testid="admin-contests-link"
            >
              <Sparkles className="w-4 h-4 mr-2" /> Admin
            </Button>
          )}
        </div>

        {loading ? (
          <p className="text-white text-center py-20">Loading contests…</p>
        ) : (
          <>
            <Section title="🔴 Live Now" empty="No contests live this week. Check back soon!">
              {active.map((c) => (
                <ContestCard key={c.contest_id} contest={c} live onClick={() => navigate(`/contests/${c.contest_id}`)} />
              ))}
            </Section>

            {past.length > 0 && (
              <Section title="🏆 Past Winners">
                {past.map((c) => (
                  <ContestCard key={c.contest_id} contest={c} onClick={() => navigate(`/contests/${c.contest_id}`)} />
                ))}
              </Section>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function Section({ title, children, empty }) {
  const items = Array.isArray(children) ? children : (children ? [children] : []);
  return (
    <div className="mb-12">
      <h2 className="text-2xl font-black text-white mb-4">{title}</h2>
      {items.length === 0 ? (
        <p className="text-pink-200/60 italic" data-testid="contests-empty">{empty}</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">{items}</div>
      )}
    </div>
  );
}

function ContestCard({ contest, live, onClick }) {
  const time = timeRemaining(contest.end_at);
  return (
    <button
      onClick={onClick}
      className="text-left rounded-lg border-4 border-black bg-gradient-to-br from-fuchsia-500 to-purple-700 p-5 shadow-[6px_6px_0_#000] hover:translate-x-1 hover:translate-y-1 hover:shadow-[2px_2px_0_#000] transition-all"
      data-testid={`contest-card-${contest.contest_id}`}
    >
      <div className="flex items-center justify-between mb-2">
        <span className="bg-black text-yellow-300 px-2 py-1 text-[10px] font-black rounded uppercase">
          {contest.category}
        </span>
        {live ? (
          <span className="bg-red-500 text-white px-2 py-1 text-[10px] font-black rounded animate-pulse">
            LIVE
          </span>
        ) : (
          <span className="bg-gray-700 text-white px-2 py-1 text-[10px] font-black rounded">
            ENDED
          </span>
        )}
      </div>
      <h3 className="text-xl font-black text-white mb-1 leading-tight">{contest.title}</h3>
      <p className="text-yellow-100 text-sm font-bold mb-3">"{contest.theme}"</p>
      <p className="text-pink-100 text-xs mb-4 line-clamp-2">{contest.description}</p>

      <div className="flex items-center gap-3 text-white text-xs font-bold border-t-2 border-black/30 pt-3">
        <span className="flex items-center gap-1"><Coins className="w-4 h-4" /> {contest.prize_first} 🥇</span>
        <span className="flex items-center gap-1"><Users className="w-4 h-4" /> {contest.entry_count ?? "—"} entries</span>
        {time && <span className="flex items-center gap-1 ml-auto"><Clock className="w-4 h-4" /> {time}</span>}
      </div>
      {contest.sponsors && contest.sponsors.length > 0 && (
        <div className="mt-2 pt-2 border-t-2 border-black/30 text-[10px] text-yellow-200 font-bold">
          Sponsored by {contest.sponsors.slice(0, 2).map(s => s.brand_name).join(", ")}
          {contest.sponsors.length > 2 && ` +${contest.sponsors.length - 2}`}
        </div>
      )}
    </button>
  );
}
